// shelfsync.js — ShelfSync storefront logic (INR base, bestsellers carousel, single product image)
const ShelfSync = (() => {
  const API_BASE = 'https://mathematics-filling-privacy-services.trycloudflare.com';
  const BASE_CURRENCY = 'INR';

  // ─── Utilities ──────────────────────────────────────────────
  function getQueryParam(name) {
    return new URLSearchParams(window.location.search).get(name) || '';
  }

  function escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // NEW: Reads the currency Shopify says the visitor has actually selected
  // (via Shopify's own currency dropdown / Markets), instead of guessing
  // from IP address. Shopify exposes this automatically as
  // window.Shopify.currency.active on every storefront page.
  // Returns null if, for some reason, Shopify hasn't set this up (so callers
  // can fall back to existing behavior safely).
  function getShopifyCurrency() {
    try {
      if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
        return window.Shopify.currency.active;
      }
    } catch {
      // no-op — fall through to null
    }
    return null;
  }

  // ─── Cart (localStorage-based, since there's no real Shopify catalog
  // until checkout) ───────────────────────────────────────────

  const CART_STORAGE_KEY = 'shelfsync_cart';

  function getCart() {
    try {
      const raw = localStorage.getItem(CART_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  function saveCart(cart) {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
    } catch {
      // localStorage unavailable (private mode, quota, etc.) — silently
      // no-op rather than crash; cart just won't persist for this visitor.
    }
    updateCartBadge();
  }

  function addToCart(item) {
    const cart = getCart();
    const existing = cart.find((i) => i.asin === item.asin);
    if (existing) {
      existing.quantity += item.quantity;
    } else {
      cart.push(item);
    }
    saveCart(cart);
  }

  function updateCartItemQuantity(asin, quantity) {
    let cart = getCart();
    if (quantity <= 0) {
      cart = cart.filter((i) => i.asin !== asin);
    } else {
      const item = cart.find((i) => i.asin === asin);
      if (item) item.quantity = quantity;
    }
    saveCart(cart);
    renderCartDrawer();
  }

  function removeFromCart(asin) {
    const cart = getCart().filter((i) => i.asin !== asin);
    saveCart(cart);
    renderCartDrawer();
  }

  function getCartCount() {
    return getCart().reduce((sum, i) => sum + i.quantity, 0);
  }

  function updateCartBadge() {
    const count = getCartCount();
    const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden="true"]');
    const bubbleWrap = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    if (count > 0) {
      if (bubble) {
        bubble.textContent = count;
      } else {
        const cartLink = document.getElementById('cart-icon-bubble');
        if (cartLink && !cartLink.querySelector('.cart-count-bubble')) {
          const div = document.createElement('div');
          div.className = 'cart-count-bubble';
          div.innerHTML = `<span aria-hidden="true">${count}</span>`;
          cartLink.appendChild(div);
        } else if (bubbleWrap) {
          bubbleWrap.innerHTML = `<span aria-hidden="true">${count}</span>`;
        }
      }
    } else if (bubbleWrap) {
      bubbleWrap.remove();
    }
  }

  // ─── Cart drawer (slide-out panel) ──────────────────────────

  const DRAWER_STYLES = `
    #ss-cart-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.4); z-index: 9998; opacity: 0; pointer-events: none; transition: opacity 0.25s ease; }
    #ss-cart-overlay.open { opacity: 1; pointer-events: auto; }
    #ss-cart-drawer { position: fixed; top: 0; right: 0; height: 100%; width: min(420px, 100%); background: #fff; z-index: 9999; box-shadow: -4px 0 24px rgba(0,0,0,0.15); transform: translateX(100%); transition: transform 0.3s ease; display: flex; flex-direction: column; }
    #ss-cart-drawer.open { transform: translateX(0); }
    .ss-cart-header { display: flex; align-items: center; justify-content: space-between; padding: 20px; border-bottom: 1px solid #eee; }
    .ss-cart-header h2 { margin: 0; font-size: 18px; }
    .ss-cart-close { background: none; border: none; font-size: 22px; cursor: pointer; line-height: 1; padding: 4px 8px; }
    .ss-cart-items { flex: 1; overflow-y: auto; padding: 12px 20px; }
    .ss-cart-item { display: flex; gap: 12px; padding: 14px 0; border-bottom: 1px solid #f0f0f0; }
    .ss-cart-item img { width: 56px; height: 84px; object-fit: cover; border-radius: 4px; background: #f5f5f5; flex-shrink: 0; }
    .ss-cart-item-info { flex: 1; min-width: 0; }
    .ss-cart-item-title { font-size: 14px; font-weight: 600; margin: 0 0 4px; }
    .ss-cart-item-price { font-size: 13px; color: #555; }
    .ss-cart-item-controls { display: flex; align-items: center; gap: 8px; margin-top: 8px; }
    .ss-cart-qty-btn { width: 24px; height: 24px; border: 1px solid #ccc; background: #fff; border-radius: 4px; cursor: pointer; font-size: 14px; line-height: 1; }
    .ss-cart-remove { font-size: 12px; color: #999; background: none; border: none; cursor: pointer; text-decoration: underline; margin-left: auto; }
    .ss-cart-empty { text-align: center; padding: 60px 20px; color: #777; }
    .ss-cart-footer { padding: 20px; border-top: 1px solid #eee; }
    .ss-cart-subtotal { display: flex; justify-content: space-between; font-size: 15px; font-weight: 600; margin-bottom: 14px; }
    .ss-cart-checkout-btn { width: 100%; padding: 14px; background: #111; color: #fff; border: none; border-radius: 6px; font-size: 15px; cursor: pointer; }
    .ss-cart-checkout-btn:disabled { opacity: 0.6; cursor: default; }
    .ss-cart-error { color: #c0392b; font-size: 13px; margin-top: 8px; text-align: center; }
  `;

  function ensureCartDrawer() {
    if (document.getElementById('ss-cart-drawer')) return;

    const style = document.createElement('style');
    style.textContent = DRAWER_STYLES;
    document.head.appendChild(style);

    const overlay = document.createElement('div');
    overlay.id = 'ss-cart-overlay';
    overlay.addEventListener('click', closeCartDrawer);

    const drawer = document.createElement('div');
    drawer.id = 'ss-cart-drawer';
    drawer.innerHTML = `
      <div class="ss-cart-header">
        <h2>Your Cart</h2>
        <button type="button" class="ss-cart-close" aria-label="Close cart">&times;</button>
      </div>
      <div class="ss-cart-items" id="ss-cart-items"></div>
      <div class="ss-cart-footer" id="ss-cart-footer"></div>
    `;
    drawer.querySelector('.ss-cart-close').addEventListener('click', closeCartDrawer);

    document.body.appendChild(overlay);
    document.body.appendChild(drawer);
  }

  function openCartDrawer() {
    ensureCartDrawer();
    renderCartDrawer();
    document.getElementById('ss-cart-overlay').classList.add('open');
    document.getElementById('ss-cart-drawer').classList.add('open');
  }

  function closeCartDrawer() {
    const overlay = document.getElementById('ss-cart-overlay');
    const drawer = document.getElementById('ss-cart-drawer');
    if (overlay) overlay.classList.remove('open');
    if (drawer) drawer.classList.remove('open');
  }

  function renderCartDrawer() {
    const itemsEl = document.getElementById('ss-cart-items');
    const footerEl = document.getElementById('ss-cart-footer');
    if (!itemsEl || !footerEl) return;

    const cart = getCart();

    if (cart.length === 0) {
      itemsEl.innerHTML = '<div class="ss-cart-empty">Your cart is empty.</div>';
      footerEl.innerHTML = '';
      return;
    }

    itemsEl.innerHTML = cart
      .map(
        (item) => `
        <div class="ss-cart-item" data-asin="${escapeHtml(item.asin)}">
          ${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" />` : '<img alt="" />'}
          <div class="ss-cart-item-info">
            <p class="ss-cart-item-title">${escapeHtml(item.title)}</p>
            <div class="ss-cart-item-price">${escapeHtml(item.symbol)} ${(item.unitAmount * item.quantity).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            <div class="ss-cart-item-controls">
              <button type="button" class="ss-cart-qty-btn" data-action="decrease">–</button>
              <span>${item.quantity}</span>
              <button type="button" class="ss-cart-qty-btn" data-action="increase">+</button>
              <button type="button" class="ss-cart-remove" data-action="remove">Remove</button>
            </div>
          </div>
        </div>`
      )
      .join('');

    itemsEl.querySelectorAll('.ss-cart-item').forEach((el) => {
      const asin = el.dataset.asin;
      const item = cart.find((i) => i.asin === asin);
      if (!item) return;
      el.querySelector('[data-action="decrease"]').addEventListener('click', () =>
        updateCartItemQuantity(asin, item.quantity - 1)
      );
      el.querySelector('[data-action="increase"]').addEventListener('click', () =>
        updateCartItemQuantity(asin, item.quantity + 1)
      );
      el.querySelector('[data-action="remove"]').addEventListener('click', () => removeFromCart(asin));
    });

    const subtotal = cart.reduce((sum, i) => sum + i.unitAmount * i.quantity, 0);
    const symbol = cart[0].symbol;

    footerEl.innerHTML = `
      <div class="ss-cart-subtotal">
        <span>Subtotal</span>
        <span>${escapeHtml(symbol)} ${subtotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
      </div>
      <button type="button" class="ss-cart-checkout-btn" id="ss-cart-checkout-btn">Proceed to Checkout</button>
      <div class="ss-cart-error" id="ss-cart-error" style="display:none;"></div>
    `;

    document.getElementById('ss-cart-checkout-btn').addEventListener('click', handleCheckout);
  }

  async function handleCheckout() {
    const btn = document.getElementById('ss-cart-checkout-btn');
    const errorEl = document.getElementById('ss-cart-error');
    const cart = getCart();
    if (cart.length === 0) return;

    btn.disabled = true;
    btn.textContent = 'Preparing checkout…';
    errorEl.style.display = 'none';

    try {
      const res = await fetch(`${API_BASE}/api/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: cart.map((i) => ({
            asin: i.asin,
            title: i.title,
            image: i.image,
            quantity: i.quantity,
            baseAmount: i.baseAmount,
            displayCurrency: i.currency,
            displayAmount: i.unitAmount,
          })),
        }),
      });
      const data = await res.json();
      if (data.success && data.cartUrl) {
        window.location.href = data.cartUrl;
      } else {
        errorEl.textContent = data.error || 'Could not start checkout. Please try again.';
        errorEl.style.display = 'block';
        btn.disabled = false;
        btn.textContent = 'Proceed to Checkout';
      }
    } catch {
      errorEl.textContent = 'Could not connect. Please try again.';
      errorEl.style.display = 'block';
      btn.disabled = false;
      btn.textContent = 'Proceed to Checkout';
    }
  }

  // Makes the theme's existing header cart icon open our drawer instead
  // of navigating to Shopify's native (empty, until checkout) cart page.
  function wireCartIcon() {
    const cartLink = document.getElementById('cart-icon-bubble');
    if (!cartLink || cartLink.dataset.ssWired) return;
    cartLink.dataset.ssWired = 'true';
    cartLink.addEventListener('click', (e) => {
      e.preventDefault();
      openCartDrawer();
    });
  }

  function revealCards(scope, selector) {
    const cards = scope.querySelectorAll(selector + ':not(.show)');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry, i) => {
        if (entry.isIntersecting) {
          setTimeout(() => entry.target.classList.add('show'), i * 40);
          io.unobserve(entry.target);
        }
      });

    }, { threshold: 0.15 });
    cards.forEach((c, i) => {
      c.style.transitionDelay = (i % 6) * 0.05 + 's';
      io.observe(c);
    });
  }

  // ─── Search Page ────────────────────────────────────────────

  function buildCard(book) {
    const pill = book.inStock
      ? '<span class="ss-pill"><span class="ss-dot"></span>In Stock</span>'
      : '<span class="ss-pill ss-pill--out"><span class="ss-dot"></span>Out of Stock</span>';

    const image = book.mainImage
      ? `<img src="${escapeHtml(book.mainImage)}" alt="${escapeHtml(book.title)}" loading="lazy" />`
      : '<div class="ss-noimg">No Cover</div>';

    const productUrl = `/pages/product?asin=${encodeURIComponent(book.asin)}`;

    return `
      <a href="${productUrl}" class="ss-card">
        <div class="ss-cover">${image}</div>
        <div class="ss-info">
          <h3 class="ss-card-title">${escapeHtml(book.title)}</h3>
          ${book.author ? `<div class="ss-card-author">${escapeHtml(book.author)}</div>` : ''}
          <div class="ss-card-format">${escapeHtml(book.binding || '')}</div>
          ${pill}
        </div>
      </a>`;
  }

  function setCount(shown, total) {
    const el = document.getElementById('ss-count');
    if (el) el.textContent = `Showing ${shown} of ${total} results`;
  }

  function appendLoadMore(container, query, token) {
    const old = container.querySelector('.ss-loadmore');
    if (old) old.remove();
    if (!token) return;

    const wrap = document.createElement('div');
    wrap.className = 'ss-loadmore';
    wrap.innerHTML = `<button id="shelfsync-load-more">Load More</button>`;
    container.appendChild(wrap);

    document.getElementById('shelfsync-load-more').addEventListener('click', async (e) => {
      const btn = e.target;
      btn.textContent = 'Loading…';
      btn.disabled = true;
      try {
        const res = await fetch(`${API_BASE}/api/search?q=${encodeURIComponent(query)}&token=${encodeURIComponent(token)}`);
        const data = await res.json();
        const grid = container.querySelector('.ss-grid');
        grid.insertAdjacentHTML('beforeend', (data.results || []).map(buildCard).join(''));
        revealCards(container, '.ss-card');
        const shown = grid.querySelectorAll('.ss-card').length;
        setCount(shown, Math.max(shown, data.total || shown));
        appendLoadMore(container, query, data.nextToken);
      } catch {
        btn.textContent = 'Failed — Try Again';
        btn.disabled = false;
      }
    });
  }

  function renderSearchResults(data, container) {
    const heading = document.getElementById('ss-heading');
    if (heading) {
      heading.innerHTML = `Results for <b>“${escapeHtml(data.query)}”</b>`;
    }

    if (!data.results || data.results.length === 0) {
      setCount(0, 0);
      container.innerHTML = `
        <div class="ss-state">
          No books found for “${escapeHtml(data.query)}”.<br><br>
          Try searching by title, author, or ISBN.
        </div>`;
      return;
    }

    setCount(data.results.length, data.total || data.results.length);
    container.innerHTML = `<div class="ss-grid">${data.results.map(buildCard).join('')}</div>`;
    revealCards(container, '.ss-card');
    appendLoadMore(container, data.query, data.nextToken);
  }

  async function initSearch() {
    const container = document.getElementById('shelfsync-search-results');
    const input = document.getElementById('shelfsync-search-input');
    const form = document.getElementById('shelfsync-search-form');
    if (!container) return;

    const query = getQueryParam('q');
    if (input && query) input.value = query;

    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const q = input ? input.value.trim() : '';
        if (q.length >= 2) {
          window.location.href = `/pages/search?q=${encodeURIComponent(q)}`;
        }
      });
    }

    if (!query || query.trim().length < 2) {
      container.innerHTML = '<div class="ss-state">Enter a title, author, or ISBN to search.</div>';
      return;
    }

    container.innerHTML = '<div class="ss-state">Searching…</div>';

    try {
      const res = await fetch(`${API_BASE}/api/search?q=${encodeURIComponent(query)}`);
      const data = await res.json();
      if (data.success) {
        renderSearchResults(data, container);
      } else {
        container.innerHTML = `<div class="ss-state ss-error">Search failed: ${escapeHtml(data.error)}</div>`;
      }
    } catch {
      container.innerHTML = '<div class="ss-state ss-error">Could not connect to search service. Please try again.</div>';
    }
  }

  // ─── Bestsellers (homepage carousel) ────────────────────────

  const ARROW_RIGHT_SVG = '<svg viewBox="0 0 24 24" fill="none"><path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const ARROW_LEFT_SVG = '<svg viewBox="0 0 24 24" fill="none"><path d="M15 6l-6 6 6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function buildBestsellerCard(book) {
    const image = book.mainImage
      ? `<img src="${escapeHtml(book.mainImage)}" alt="${escapeHtml(book.title)}" loading="lazy" />`
      : '<div class="ss-noimg">No Cover</div>';

    return `
      <a href="/pages/product?asin=${encodeURIComponent(book.asin)}" class="ssb-card">
        <div class="ssb-cover">${image}</div>
        <div class="ssb-info">
          <h3 class="ssb-card-title">${escapeHtml(book.title)}</h3>
          ${book.author ? `<div class="ssb-author">${escapeHtml(book.author)}</div>` : ''}
        </div>
      </a>`;
  }

  function buildBestsellerList(list) {
    const cards = list.books.map(buildBestsellerCard).join('');
    return `
      <div class="ssb-list" data-list-id="${escapeHtml(list.id)}">
        <div class="ssb-head">
          <div class="ssb-head-left">
            <h2 class="ssb-title">${escapeHtml(list.title)}</h2>
          </div>
          <a class="ssb-seeall" href="/pages/search?q=${encodeURIComponent(list.title)}">See All</a>
        </div>
        <div class="ssb-viewport">
          <div class="ssb-row">${cards}</div>
          <div class="ssb-nav ssb-nav--prev" style="display:none;">
            <button type="button" aria-label="Scroll left">${ARROW_LEFT_SVG}</button>
          </div>
          <div class="ssb-nav ssb-nav--next">
            <button type="button" aria-label="Scroll right">${ARROW_RIGHT_SVG}</button>
          </div>
        </div>
      </div>`;
  }

  function wireCarousel(listEl) {
    const row = listEl.querySelector('.ssb-row');
    const prevNav = listEl.querySelector('.ssb-nav--prev');
    const nextNav = listEl.querySelector('.ssb-nav--next');
    if (!row) return;

    const scrollAmount = () => row.clientWidth * 0.85;

    prevNav.querySelector('button').addEventListener('click', () => {
      row.scrollBy({ left: -scrollAmount(), behavior: 'smooth' });
    });
    nextNav.querySelector('button').addEventListener('click', () => {
      row.scrollBy({ left: scrollAmount(), behavior: 'smooth' });
    });

    function updateNavVisibility() {
      const maxScroll = row.scrollWidth - row.clientWidth - 2;
      prevNav.style.display = row.scrollLeft > 4 ? 'flex' : 'none';
      nextNav.style.display = row.scrollLeft < maxScroll ? 'flex' : 'none';
    }

    row.addEventListener('scroll', updateNavVisibility);
    window.addEventListener('resize', updateNavVisibility);
    updateNavVisibility();
  }

  async function initBestsellers() {
    const container = document.getElementById('shelfsync-bestsellers-container');
    if (!container) return;

    try {
      const res = await fetch(`${API_BASE}/api/bestsellers`);
      const data = await res.json();

      if (!data.success || !data.lists || data.lists.length === 0) {
        container.innerHTML = '<div class="ssb-state">Bestsellers are unavailable right now.</div>';
        return;
      }

      container.innerHTML = data.lists.map(buildBestsellerList).join('');

      container.querySelectorAll('.ssb-list').forEach(wireCarousel);
      revealCards(container, '.ssb-card');
    } catch {
      container.innerHTML = '<div class="ssb-state">Could not load bestsellers.</div>';
    }
  }

  // ─── Product Page ───────────────────────────────────────────

  let qty = 1;
  let unitDisplayAmount = null;

  function renderPriceValue() {
    const el = document.getElementById('ss-price-digits');
    if (!el || unitDisplayAmount === null) return;
    el.textContent = (unitDisplayAmount * qty).toLocaleString(undefined, {
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    });
  }

  async function initProduct() {
    const container = document.getElementById('shelfsync-product-container');
    if (!container) return;

    const asin = getQueryParam('asin');
    if (!asin) {
      container.innerHTML = '<div class="ss-state ss-error">No product specified.</div>';
      return;
    }

    container.innerHTML = '<div class="ss-state">Loading product…</div>';

    // NEW: pass along the currency Shopify says the visitor has selected
    // (from the store's own currency dropdown/Markets), instead of letting
    // the backend guess based on IP address. If Shopify hasn't exposed a
    // currency for some reason, we simply don't send the parameter and the
    // backend falls back to its existing behavior — nothing breaks either way.
    const shopifyCurrency = getShopifyCurrency();
    const productUrl = shopifyCurrency
      ? `${API_BASE}/api/product/${encodeURIComponent(asin)}?currency=${encodeURIComponent(shopifyCurrency)}`
      : `${API_BASE}/api/product/${encodeURIComponent(asin)}`;

    try {
      const res = await fetch(productUrl);
      const data = await res.json();

      if (!data.success) {
        container.innerHTML = '<div class="ss-state ss-error">Product not found.</div>';
        return;
      }

      const book = data.data;

      let priceInner = '';
      const dp = book.displayPrice;
      let unitDisplayCurrency = BASE_CURRENCY;
      if (dp) {
        unitDisplayAmount = dp.amount;
        unitDisplayCurrency = dp.currency;
        priceInner = `<div class="ss-price">${escapeHtml(dp.symbol)} <span id="ss-price-digits">0.00</span></div>`;
      } else if (book.buyBoxPrice !== null && book.buyBoxPrice !== undefined) {
        unitDisplayAmount = book.buyBoxPrice;
        priceInner = `<div class="ss-price">₹ <span id="ss-price-digits">0.00</span></div>`;
      } else {
        unitDisplayAmount = null;
        priceInner = `<div class="ss-price" style="font-size:16px;">Price unavailable</div>`;
      }

      // Purchasable requires BOTH inStock AND a valid price —
      // a null price means Amazon has no available offer right now.
      // The stock pill and the Add to Cart button must always agree.
      const canBuy = book.inStock && unitDisplayAmount !== null;

      const stockHtml = canBuy
        ? '<div class="ss-stock"><span class="ss-dot"></span>In Stock</div>'
        : '<div class="ss-stock ss-stock--out"><span class="ss-dot"></span>Out of Stock</div>';

      const mainImage = book.mainImage || (book.images || [])[0] || '';

      const specs = [
        ['Author', book.author],
        ['Publisher', book.publisher],
        ['Publication Date', book.publicationDate],
        ['Language', book.language],
        ['Pages', book.pages ? String(book.pages) : ''],
        ['Binding', book.binding],
        ['ISBN', book.isbn],
        ['EAN', book.ean],
      ].filter(([, v]) => v)
        .map(([label, value]) =>
          `<div class="ss-spec"><div class="ss-spec-label">${escapeHtml(label)}</div><div class="ss-spec-value">${escapeHtml(value)}</div></div>`
        ).join('');

      container.innerHTML = `
        <div class="ss-wrap">
          <div class="ss-stage">
            <div class="ss-frame" id="ss-frame">
              ${mainImage ? `<img id="ss-main-img" src="${escapeHtml(mainImage)}" alt="${escapeHtml(book.title)}" />` : '<div class="ss-noimg">No Cover</div>'}
            </div>
          </div>
          <div class="ss-info">
            <div class="ss-eyebrow">${canBuy ? 'In Stock' : 'Currently Unavailable'}</div>
            <h1 class="ss-h1">${escapeHtml(book.title)}</h1>
            ${book.author ? `<div class="ss-by">by <b>${escapeHtml(book.author)}</b></div>` : ''}
            <div class="ss-price-row">
              ${priceInner}
              ${stockHtml}
            </div>
            <div class="ss-divider"></div>
            <div class="ss-buy">
              <div class="ss-qty">
                <button type="button" id="ss-qty-minus">–</button>
                <span id="ss-qty-val">1</span>
                <button type="button" id="ss-qty-plus">+</button>
              </div>
              <button class="ss-cta" id="ss-cta" ${canBuy ? '' : 'disabled'}>
                <span>${canBuy ? 'Add to Cart' : 'Out of Stock'}</span>
              </button>
            </div>
            ${book.description ? `<p class="ss-desc">${escapeHtml(book.description).replace(/\n/g, '<br>')}</p>` : ''}
            ${specs ? `<div class="ss-eyebrow" style="opacity:1;animation:none;margin-bottom:12px;">Book Details</div><div class="ss-specs">${specs}</div>` : ''}
          </div>
        </div>`;

      qty = 1;
      let pStart = null;
      function countUp(ts) {
        if (!pStart) pStart = ts;
        const t = Math.min(1, (ts - pStart) / 900);
        const el = document.getElementById('ss-price-digits');
        if (el && unitDisplayAmount !== null) {
          el.textContent = (unitDisplayAmount * qty * t).toLocaleString(undefined, {
            minimumFractionDigits: 2, maximumFractionDigits: 2,
          });
        }
        if (t < 1) requestAnimationFrame(countUp);
      }
      if (unitDisplayAmount !== null) requestAnimationFrame(countUp);

      const qtyVal = document.getElementById('ss-qty-val');
      document.getElementById('ss-qty-minus').addEventListener('click', () => {
        qty = Math.max(1, qty - 1);
        qtyVal.textContent = qty;
        renderPriceValue();
      });
      document.getElementById('ss-qty-plus').addEventListener('click', () => {
        qty = Math.min(10, qty + 1);
        qtyVal.textContent = qty;
        renderPriceValue();
      });

      const frame = document.getElementById('ss-frame');
      if (frame && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        frame.addEventListener('mousemove', (e) => {
          const rect = frame.getBoundingClientRect();
          const px = (e.clientX - rect.left) / rect.width - 0.5;
          const py = (e.clientY - rect.top) / rect.height - 0.5;
          frame.style.transform = `perspective(900px) rotateY(${px * 8}deg) rotateX(${-py * 8}deg) scale(1.02)`;
        });
        frame.addEventListener('mouseleave', () => {
          frame.style.transform = 'none';
        });
      }

      const specRows = container.querySelectorAll('.ss-spec');
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('show');
        });
      }, { threshold: 0.2 });
      specRows.forEach((r, i) => {
        r.style.transitionDelay = (i * 0.05) + 's';
        io.observe(r);
      });

      const cta = document.getElementById('ss-cta');
      if (cta && canBuy) {
        cta.addEventListener('click', () => {
          // Capture the exact INR base price backing what's displayed right
          // now — checkout will use THIS number (converted to USD for
          // Shopify), never a freshly re-fetched Amazon price. This keeps
          // the price the customer saw identical to what they're charged,
          // rather than it silently changing if Amazon's live price moves
          // between "Add to Cart" and "Proceed to Checkout."
          const baseAmount = dp ? dp.baseAmount : book.buyBoxPrice;
          addToCart({
            asin: book.asin,
            title: book.title,
            image: book.mainImage,
            quantity: qty,
            unitAmount: unitDisplayAmount,
            currency: unitDisplayCurrency,
            symbol: dp ? dp.symbol : escapeHtml(BASE_CURRENCY),
            baseAmount,
          });
          const label = cta.querySelector('span');
          const originalText = label.textContent;
          label.textContent = 'Added!';
          setTimeout(() => {
            label.textContent = originalText;
          }, 1200);
          openCartDrawer();
        });
      }

    } catch {
      container.innerHTML = '<div class="ss-state ss-error">Failed to load product. Please try again.</div>';
    }
  }

  // ─── Currency/country switcher fix ──────────────────────────

  // Shopify's built-in currency/country selector redirects you back to
  // the current page after switching — but it only remembers the PATH,
  // and Shopify's server strips off everything after the "?" no matter
  // what we tell it to remember. That's why switching currency on a
  // product page was landing on an unrelated/default book instead of
  // returning to the same one — our ?asin=... was being silently dropped.
  //
  // IMPORTANT: the theme's own localization-form.js handles the click on
  // a country/currency option by calling form.submit() directly in
  // JavaScript. A JS-triggered form.submit() call does NOT fire a
  // "submit" event on the form at all (this is standard browser
  // behavior) — so listening for "submit" (an earlier attempt) never
  // actually ran. The reliable fix has to intercept the CLICK itself,
  // and it has to run BEFORE the theme's own click handler does, so we
  // can stop it from calling form.submit() in the first place.
  //
  // We do that by listening on `document` in the "capture phase" (the
  // `true` third argument below) — capture-phase listeners on an
  // ancestor always run before any listener on the clicked element
  // itself, which lets us intercept the click before the theme's own
  // code sees it. Then we submit the currency change quietly ourselves
  // in the background, and navigate back to the EXACT original URL
  // (path + query string) using our own JavaScript — bypassing
  // Shopify's redirect, and its query-string stripping, entirely.
  function fixLocalizationRedirect() {
    document.addEventListener(
      'click',
      (e) => {
        const link = e.target.closest('form.localization-form a[data-value]');
        if (!link) return;
        const form = link.closest('form.localization-form');
        if (!form) return;

        e.preventDefault();
        e.stopImmediatePropagation();

        const input = form.querySelector('input[name="country_code"], input[name="locale_code"]');
        if (input) input.value = link.dataset.value;

        const fullPath = window.location.pathname + window.location.search;
        const formData = new FormData(form);
        const goBack = () => {
          window.location.href = fullPath;
        };

        fetch(form.action || window.location.pathname, {
          method: 'POST',
          body: formData,
          credentials: 'same-origin',
        })
          .then(goBack)
          .catch(goBack);
      },
      true // capture phase — must run before the theme's own click handler
    );
  }

  // ─── Init ───────────────────────────────────────────────────

  function init() {
    fixLocalizationRedirect();
    ensureCartDrawer();
    wireCartIcon();
    updateCartBadge();
    if (document.getElementById('shelfsync-search-results')) initSearch();
    if (document.getElementById('shelfsync-product-container')) initProduct();
    if (document.getElementById('shelfsync-bestsellers-container')) initBestsellers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  return { init };
})();